<template>
  <!-- eslint-disable vue/max-attributes-per-line -->
  <div>
    <v-breadcrumbs :items="breadcrumbs" divider="/" class="pa-0 mb-4" />

    <div class="d-flex align-start mb-6 flex-wrap webhook-page-header">
      <div class="flex-grow-1">
        <p class="text-2xl mb-1">
          Уведомления YClients
        </p>
        <p class="text--secondary mb-0">
          Настройки уведомлений сотрудникам о событиях YClients
        </p>
      </div>
      <v-btn v-if="tab === 0" color="primary" :disabled="metadataLoading" @click="openCreate">
        <v-icon left small>
          {{ icons.mdiPlus }}
        </v-icon>
        Новое правило
      </v-btn>
    </div>

    <v-tabs v-model="tab" class="mb-5">
      <v-tab>Правила</v-tab>
      <v-tab>Журнал срабатываний</v-tab>
    </v-tabs>

    <v-alert v-if="pageError" type="error" text dense dismissible @input="pageError = ''">
      {{ pageError }}
    </v-alert>

    <div v-show="tab === 0">
      <v-card flat outlined class="mb-4 pa-4">
        <v-row dense align="center">
          <v-col cols="12" sm="5" md="4">
            <v-select
              v-model="ruleFilters.resource"
              :items="metadata.resources"
              item-text="label"
              item-value="value"
              label="Тип события"
              outlined
              dense
              hide-details
              clearable
            />
          </v-col>
          <v-col cols="12" sm="4" md="3">
            <v-select
              v-model="ruleFilters.is_active"
              :items="activeOptions"
              item-text="text"
              item-value="value"
              label="Активность"
              outlined
              dense
              hide-details
              clearable
            />
          </v-col>
          <v-col cols="12" sm="3" class="d-flex webhook-filter-actions">
            <v-btn color="primary" :loading="rulesLoading" @click="loadRules">
              Применить
            </v-btn>
            <v-btn text :disabled="rulesLoading" @click="resetRuleFilters">
              Сбросить
            </v-btn>
          </v-col>
        </v-row>
      </v-card>

      <v-card v-if="metadataLoading && !metadata.resources.length" flat outlined class="pa-4">
        <v-progress-linear indeterminate color="primary" class="mb-4" />
        <v-skeleton-loader type="table" />
      </v-card>

      <v-card v-else flat outlined>
        <v-data-table
          :headers="ruleHeaders"
          :items="rules"
          :loading="rulesLoading"
          item-key="id"
          :items-per-page="20"
          no-data-text="Правила не найдены"
          class="webhook-rules-table"
        >
          <template #[`item.resource`]="{ item }">
            {{ item.resource_title || resourceTitle(item.resource) }}
          </template>
          <template #[`item.match_type`]="{ item }">
            <v-chip x-small label outlined>
              {{ item.match_type_title || matchTypeTitle(item.match_type) }}
            </v-chip>
          </template>
          <template #[`item.conditions`]="{ item }">
            {{ (item.conditions || []).length }}
          </template>
          <template #[`item.recipients`]="{ item }">
            <div v-if="item.recipients && item.recipients.length" class="py-2">
              <div v-for="recipient in item.recipients" :key="recipient.id || recipient.email" class="text-no-wrap">
                {{ recipient.email }}
              </div>
            </div>
            <span v-else>—</span>
          </template>
          <template #[`item.is_active`]="{ item }">
            <v-chip :color="item.is_active ? 'success' : 'default'" x-small label>
              {{ item.is_active ? 'Активно' : 'Выключено' }}
            </v-chip>
          </template>
          <template #[`item.updated_at`]="{ item }">
            <span class="text-no-wrap">{{ formatDateTime(item.updated_at) }}</span>
          </template>
          <template #[`item.actions`]="{ item }">
            <div class="d-flex flex-nowrap">
              <v-btn icon small title="Проверить" @click="openDryRun(item)">
                <v-icon small>
                  {{ icons.mdiPlayOutline }}
                </v-icon>
              </v-btn>
              <v-btn icon small title="Редактировать" :loading="openingId === item.id" @click="openEdit(item)">
                <v-icon small>
                  {{ icons.mdiPencilOutline }}
                </v-icon>
              </v-btn>
              <v-btn icon small color="error" title="Удалить" @click="requestDelete(item)">
                <v-icon small>
                  {{ icons.mdiDeleteOutline }}
                </v-icon>
              </v-btn>
            </div>
          </template>
        </v-data-table>
      </v-card>
    </div>

    <div v-show="tab === 1">
      <v-card flat outlined class="mb-4 pa-4">
        <v-row dense>
          <v-col cols="12" sm="6" md="2">
            <v-select
              v-model="triggerFilters.rule_id"
              :items="rules"
              item-text="name"
              item-value="id"
              label="Правило"
              outlined
              dense
              hide-details
              clearable
            />
          </v-col>
          <v-col cols="12" sm="6" md="2">
            <v-select
              v-model="triggerFilters.resource"
              :items="metadata.resources"
              item-text="label"
              item-value="value"
              label="Тип события"
              outlined
              dense
              hide-details
              clearable
            />
          </v-col>
          <v-col cols="12" sm="6" md="2">
            <v-select
              v-model="triggerFilters.status"
              :items="triggerStatuses"
              item-text="text"
              item-value="value"
              label="Статус доставки"
              outlined
              dense
              hide-details
              clearable
            />
          </v-col>
          <v-col cols="12" sm="6" md="2">
            <v-text-field
              v-model="triggerFilters.date_from"
              type="datetime-local"
              label="Дата с"
              outlined
              dense
              hide-details
            />
          </v-col>
          <v-col cols="12" sm="6" md="2">
            <v-text-field
              v-model="triggerFilters.date_to"
              type="datetime-local"
              label="Дата по"
              outlined
              dense
              hide-details
            />
          </v-col>
          <v-col cols="12" sm="6" md="2" class="d-flex webhook-filter-actions">
            <v-btn color="primary" :loading="triggersLoading" @click="loadTriggers">
              Применить
            </v-btn>
            <v-btn
              icon
              :disabled="triggersLoading"
              title="Сбросить фильтры"
              @click="resetTriggerFilters"
            >
              <v-icon>{{ icons.mdiRefresh }}</v-icon>
            </v-btn>
          </v-col>
        </v-row>
      </v-card>

      <v-card flat outlined>
        <v-data-table
          :headers="triggerHeaders"
          :items="triggers"
          :loading="triggersLoading"
          item-key="id"
          :items-per-page="100"
          hide-default-footer
          no-data-text="Срабатывания не найдены"
          class="trigger-table"
        >
          <template #[`item.created_at`]="{ item }">
            <span class="text-no-wrap">{{ formatDateTime(item.created_at) }}</span>
          </template>
          <template #[`item.event_datetime`]="{ item }">
            <span class="text-no-wrap">{{ formatDateTime(item.event_datetime) }}</span>
          </template>
          <template #[`item.resource`]="{ item }">
            {{ resourceTitle(item.resource) }}
          </template>
          <template #[`item.company_id`]="{ item }">
            {{ companyTitle(item.company_id) }}
          </template>
          <template #[`item.webhook_status`]="{ item }">
            {{ item.webhook_status || '—' }}
          </template>
          <template #[`item.status`]="{ item }">
            <v-chip :color="statusColor(item.status)" dark x-small label>
              {{ item.status_title || item.status }}
            </v-chip>
          </template>
          <template #[`item.matched_values`]="{ item }">
            <pre class="webhook-json-cell">{{ formatJson(item.matched_values) }}</pre>
          </template>
          <template #[`item.notification_ids`]="{ item }">
            {{ (item.notification_ids || []).join(', ') || '—' }}
          </template>
          <template #[`item.error`]="{ item }">
            <span class="error--text">{{ item.error || '—' }}</span>
          </template>
        </v-data-table>
      </v-card>
    </div>

    <v-dialog
      v-model="formDialog"
      :fullscreen="$vuetify.breakpoint.xsOnly"
      max-width="1100"
      scrollable
      :persistent="saving"
    >
      <v-card v-if="form">
        <v-form @submit.prevent="saveRule">
          <v-card-title>
            {{ form.id ? 'Редактирование правила' : 'Новое правило' }}
          </v-card-title>
          <v-divider />
          <v-card-text class="pt-5">
            <v-alert v-if="formError" type="error" text dense>
              {{ formError }}
            </v-alert>

            <v-row dense>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="form.name"
                  label="Название *"
                  outlined
                  dense
                  :disabled="saving"
                  :error-messages="fieldError('name')"
                  @input="clearFieldError('name')"
                />
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <v-select
                  :value="form.resource"
                  :items="metadata.resources"
                  item-text="label"
                  item-value="value"
                  label="Тип события *"
                  outlined
                  dense
                  :disabled="saving"
                  :error-messages="fieldError('resource')"
                  @change="requestResourceChange"
                />
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <v-select
                  v-model="form.match_type"
                  :items="metadata.match_types"
                  item-text="label"
                  item-value="value"
                  label="Логика условий *"
                  outlined
                  dense
                  :disabled="saving"
                  :error-messages="fieldError('match_type')"
                  @change="clearFieldError('match_type')"
                />
              </v-col>
            </v-row>

            <div class="d-flex align-center mb-3">
              <div>
                <div class="text-subtitle-1 font-weight-medium">
                  Условия
                </div>
                <div class="text-caption text--secondary">
                  Добавьте хотя бы одно условие
                </div>
              </div>
              <v-spacer />
              <v-btn small color="primary" outlined :disabled="saving || !form.resource" @click="addCondition">
                <v-icon left small>
                  {{ icons.mdiPlus }}
                </v-icon>
                Условие
              </v-btn>
            </div>

            <v-alert v-if="formErrors.conditions" type="error" text dense>
              {{ formErrors.conditions }}
            </v-alert>
            <v-alert v-if="!form.conditions.length" type="info" text dense>
              Условия ещё не добавлены
            </v-alert>

            <v-card
              v-for="(condition, index) in form.conditions"
              :key="condition.localKey"
              flat
              outlined
              class="mb-3 pa-3 condition-card"
            >
              <v-row dense align="center" class="condition-row">
                <v-col cols="12" sm="6" md="4">
                  <v-select
                    v-model="condition.field"
                    :items="currentFields"
                    item-text="title"
                    item-value="key"
                    label="Поле *"
                    outlined
                    dense
                    hide-details
                    :disabled="saving"
                    @change="onConditionFieldChange(condition)"
                  >
                    <template v-if="condition.field === 'status'" #append-outer>
                      <v-tooltip top>
                        <template #activator="{ on, attrs }">
                          <v-icon
                            v-bind="attrs"
                            small
                            class="status-help-icon"
                            v-on="on"
                          >
                            {{ icons.mdiHelpCircleOutline }}
                          </v-icon>
                        </template>
                        <div>create — создание;</div>
                        <div>update — изменение;</div>
                        <div>delete — удаление.</div>
                      </v-tooltip>
                    </template>
                  </v-select>
                </v-col>
                <v-col cols="12" sm="6" md="3">
                  <v-select
                    v-model="condition.operator"
                    :items="operatorsFor(condition)"
                    item-text="label"
                    item-value="value"
                    label="Оператор *"
                    outlined
                    dense
                    hide-details
                    :disabled="saving || !condition.field"
                    @change="onConditionOperatorChange(condition)"
                  />
                </v-col>
                <v-col
                  cols="12"
                  sm="6"
                  md="3"
                  :class="{ 'd-none d-md-block': !showsConditionValue(condition) }"
                >
                  <v-select
                    v-if="showsConditionValue(condition) && condition.field === 'status'"
                    v-model="condition.value"
                    :items="eventStatusOptions"
                    item-text="text"
                    item-value="value"
                    :multiple="isMultiValue(condition)"
                    :chips="isMultiValue(condition)"
                    small-chips
                    label="Значение *"
                    outlined
                    dense
                    hide-details
                    :disabled="saving"
                  >
                    <template #append-outer>
                      <v-tooltip top>
                        <template #activator="{ on, attrs }">
                          <v-icon
                            v-bind="attrs"
                            small
                            class="status-help-icon"
                            v-on="on"
                          >
                            {{ icons.mdiHelpCircleOutline }}
                          </v-icon>
                        </template>
                        <div>create — создание;</div>
                        <div>update — изменение;</div>
                        <div>delete — удаление.</div>
                      </v-tooltip>
                    </template>
                  </v-select>
                  <v-select
                    v-else-if="showsConditionValue(condition) && condition.field === 'company_id'"
                    v-model="condition.value"
                    :items="metadata.companies"
                    item-text="label"
                    item-value="value"
                    :multiple="isMultiValue(condition)"
                    :chips="isMultiValue(condition)"
                    label="Значение *"
                    outlined
                    dense
                    hide-details
                    :disabled="saving"
                  />
                  <v-select
                    v-else-if="showsConditionValue(condition) && conditionField(condition) && conditionField(condition).type === 'boolean'"
                    v-model="condition.value"
                    :items="booleanOptions"
                    item-text="text"
                    item-value="value"
                    :multiple="isMultiValue(condition)"
                    label="Значение *"
                    outlined
                    dense
                    hide-details
                    :disabled="saving"
                  />
                  <v-combobox
                    v-else-if="showsConditionValue(condition) && isMultiValue(condition)"
                    v-model="condition.value"
                    multiple
                    chips
                    small-chips
                    deletable-chips
                    label="Значения *"
                    placeholder="Введите и нажмите Enter"
                    outlined
                    dense
                    hide-details
                    :disabled="saving"
                  />
                  <v-text-field
                    v-else-if="showsConditionValue(condition)"
                    v-model="condition.value"
                    :type="isNumericCondition(condition) ? 'number' : 'text'"
                    label="Значение *"
                    outlined
                    dense
                    hide-details
                    :disabled="saving"
                  >
                    <template v-if="condition.field === 'sale.good_title'" #append-outer>
                      <v-tooltip top max-width="360">
                        <template #activator="{ on, attrs }">
                          <v-icon
                            v-bind="attrs"
                            small
                            class="status-help-icon"
                            v-on="on"
                          >
                            {{ icons.mdiHelpCircleOutline }}
                          </v-icon>
                        </template>
                        <div class="mb-1">
                          Название товара можно просто скопировать из YClients.
                        </div>
                        <div>contains — можно указать часть названия;</div>
                        <div>eq — нужно полное точное название;</div>
                        <div>not_contains — название не содержит указанный текст.</div>
                        <div class="mt-1">
                          Регистр учитывается.
                        </div>
                      </v-tooltip>
                    </template>
                  </v-text-field>
                </v-col>
                <v-col cols="12" sm="6" md="2" class="d-flex justify-end flex-nowrap condition-actions">
                  <v-btn icon small :disabled="saving || index === 0" title="Выше" @click="moveCondition(index, -1)">
                    <v-icon small>
                      {{ icons.mdiArrowUp }}
                    </v-icon>
                  </v-btn>
                  <v-btn
                    icon
                    small
                    :disabled="saving || index === form.conditions.length - 1"
                    title="Ниже"
                    @click="moveCondition(index, 1)"
                  >
                    <v-icon small>
                      {{ icons.mdiArrowDown }}
                    </v-icon>
                  </v-btn>
                  <v-btn icon small color="error" :disabled="saving" title="Удалить" @click="removeCondition(index)">
                    <v-icon small>
                      {{ icons.mdiClose }}
                    </v-icon>
                  </v-btn>
                </v-col>
              </v-row>
            </v-card>

            <v-divider class="my-5" />

            <div class="text-subtitle-1 font-weight-medium mb-2">
              Получатели
            </div>

            <v-card v-if="selectedRecipients.length" flat outlined class="mb-3 recipients-card">
              <v-list class="pa-0">
                <v-list-item
                  v-for="recipient in selectedRecipients"
                  :key="recipient.email"
                  class="selected-recipient"
                >
                  <v-list-item-content>
                    <v-list-item-title class="font-weight-medium">
                      {{ recipient.email }}
                    </v-list-item-title>
                  </v-list-item-content>
                  <v-list-item-action>
                    <v-btn
                      icon
                      small
                      color="error"
                      :disabled="saving"
                      title="Удалить получателя"
                      @click="removeRecipient(recipient)"
                    >
                      <v-icon small>
                        {{ icons.mdiClose }}
                      </v-icon>
                    </v-btn>
                  </v-list-item-action>
                </v-list-item>
              </v-list>
            </v-card>
            <v-alert v-else type="info" text dense class="mb-4">
              Получатели ещё не добавлены
            </v-alert>

            <div class="text-subtitle-2 mb-2">
              Добавить получателя
            </div>
            <div class="d-flex align-start recipient-email-control">
              <v-text-field
                v-model.trim="recipientEmail"
                type="email"
                label="Введите email"
                outlined
                dense
                :disabled="saving"
                :error-messages="recipientEmailError ? [recipientEmailError] : fieldError('recipients')"
                @input="clearFieldError('recipients')"
                @keydown.enter.prevent="addRecipientEmail"
              />
              <v-btn
                height="40"
                color="primary"
                :disabled="saving || !isValidEmail(recipientEmail)"
                @click="addRecipientEmail"
              >
                Добавить
              </v-btn>
            </div>

            <v-divider class="my-5" />

            <div class="text-subtitle-1 font-weight-medium mb-2">
              Шаблон сообщения
            </div>
            <v-textarea
              ref="messageTemplate"
              v-model="form.message_template"
              label="Текст сообщения"
              hint="Можно оставить пустым — backend сформирует стандартное сообщение"
              persistent-hint
              outlined
              rows="4"
              :disabled="saving"
              :error-messages="fieldError('message_template')"
              @input="clearFieldError('message_template')"
            />
            <div class="d-flex align-center flex-wrap template-fields">
              <span class="text-caption text--secondary mr-2">Переменные:</span>
              <v-chip
                v-for="field in metadata.template_fields"
                :key="field"
                x-small
                outlined
                label
                :title="'Вставить {' + field + '}'"
                @click="insertTemplateField(field)"
              >
                {{ templateFieldTitle(field) }}
              </v-chip>
            </div>

            <v-switch
              v-model="form.is_active"
              label="Правило активно"
              hide-details
              class="mt-5"
              :disabled="saving"
            />
          </v-card-text>
          <v-divider />
          <v-card-actions class="pa-4">
            <v-btn text :disabled="saving" @click="openDryRun()">
              <v-icon left small>
                {{ icons.mdiPlayOutline }}
              </v-icon>
              Dry-run
            </v-btn>
            <v-spacer />
            <v-btn text :disabled="saving" @click="formDialog = false">
              Отмена
            </v-btn>
            <v-btn color="primary" type="submit" :loading="saving" :disabled="saving">
              Сохранить
            </v-btn>
          </v-card-actions>
        </v-form>
      </v-card>
    </v-dialog>

    <v-dialog v-model="deleteDialog" max-width="460" :persistent="deleting">
      <v-card>
        <v-card-title>Удалить правило?</v-card-title>
        <v-card-text v-if="deleteTarget">
          Правило «{{ deleteTarget.name }}» будет удалено без возможности восстановления.
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text :disabled="deleting" @click="deleteDialog = false">
            Отмена
          </v-btn>
          <v-btn color="error" :loading="deleting" :disabled="deleting" @click="confirmDelete">
            Удалить
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="resourceDialog" max-width="500">
      <v-card>
        <v-card-title>Сменить тип события?</v-card-title>
        <v-card-text>
          Текущие условия будут удалены, так как набор доступных полей изменится.
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="pendingResource = null; resourceDialog = false">
            Отмена
          </v-btn>
          <v-btn color="primary" @click="confirmResourceChange">
            Сменить
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="dryRunDialog" max-width="900" scrollable :persistent="dryRunLoading">
      <v-card>
        <v-card-title>Dry-run правила</v-card-title>
        <v-card-text class="pt-4">
          <v-alert v-if="dryRunError" type="error" text dense>
            {{ dryRunError }}
          </v-alert>
          <v-textarea
            v-model="dryRunJson"
            label="Тело webhook в формате JSON"
            outlined
            rows="12"
            spellcheck="false"
            :disabled="dryRunLoading"
            :error-messages="dryRunJsonError ? [dryRunJsonError] : []"
            @input="dryRunJsonError = ''; dryRunResult = null"
          />
          <v-card v-if="dryRunResult" flat outlined class="pa-4">
            <div class="d-flex align-center mb-3">
              <span class="font-weight-medium mr-3">Совпадение:</span>
              <v-chip :color="dryRunResult.matched ? 'success' : 'warning'" dark small>
                {{ dryRunResult.matched ? 'Да' : 'Нет' }}
              </v-chip>
            </div>
            <div class="text-subtitle-2">
              Фактические значения
            </div>
            <pre class="webhook-result">{{ formatJson(dryRunResult.matched_values) }}</pre>
            <div class="text-subtitle-2 mt-3">
              План действий
            </div>
            <ul v-if="dryRunResult.actions_preview && dryRunResult.actions_preview.length">
              <li v-for="action in dryRunResult.actions_preview" :key="action">
                {{ action }}
              </li>
            </ul>
            <span v-else class="text--secondary">Действий нет</span>
          </v-card>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text :disabled="dryRunLoading" @click="dryRunDialog = false">
            Закрыть
          </v-btn>
          <v-btn color="primary" :loading="dryRunLoading" :disabled="dryRunLoading" @click="runDryRun">
            Проверить
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar" :color="snackbarColor" timeout="5000">
      {{ snackbarText }}
      <template #action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar = false">
          OK
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import {
  mdiArrowDown,
  mdiArrowUp,
  mdiClose,
  mdiDeleteOutline,
  mdiHelpCircleOutline,
  mdiPencilOutline,
  mdiPlayOutline,
  mdiPlus,
  mdiRefresh,
} from '@mdi/js'
import Api from '@/api/api'

const EMPTY_OPERATORS = ['empty', 'not_empty']
const LIST_OPERATORS = ['in', 'not_in']
const TRIGGER_STATUSES = [
  { value: 'PROCESSING', text: 'В обработке' },
  { value: 'SENT', text: 'Отправлено' },
  { value: 'PARTIAL', text: 'Частично отправлено' },
  { value: 'SKIPPED', text: 'Пропущено' },
  { value: 'FAILED', text: 'Ошибка' },
]

let conditionKey = 0

function emptyMetadata() {
  return {
    resources: [],
    match_types: [],
    operators: [],
    fields: [],
    companies: [],
    template_fields: [],
  }
}

function newCondition(data = {}) {
  conditionKey += 1
  return {
    localKey: conditionKey,
    field: data.field || '',
    operator: data.operator || '',
    value: data.value === undefined ? null : data.value,
  }
}

function cleanParams(params) {
  const result = {}
  Object.entries(params).forEach(([key, value]) => {
    if (value !== '' && value !== null && value !== undefined) {
      result[key] = value
    }
  })
  return result
}

export default {
  name: 'WebhookRules',
  data() {
    return {
      icons: {
        mdiArrowDown,
        mdiArrowUp,
        mdiClose,
        mdiDeleteOutline,
        mdiHelpCircleOutline,
        mdiPencilOutline,
        mdiPlayOutline,
        mdiPlus,
        mdiRefresh,
      },
      breadcrumbs: [
        { text: 'Главная', to: '/dashboard', disabled: false },
        { text: 'Уведомления YClients', disabled: true },
      ],
      tab: 0,
      metadata: emptyMetadata(),
      metadataLoading: false,
      rules: [],
      rulesLoading: false,
      ruleFilters: { resource: '', is_active: null },
      activeOptions: [
        { text: 'Активные', value: true },
        { text: 'Выключенные', value: false },
      ],
      booleanOptions: [
        { text: 'Да', value: true },
        { text: 'Нет', value: false },
      ],
      eventStatusOptions: [
        { text: 'create', value: 'create' },
        { text: 'update', value: 'update' },
        { text: 'delete', value: 'delete' },
      ],
      ruleHeaders: [
        { text: 'Название', value: 'name' },
        { text: 'Тип события', value: 'resource' },
        { text: 'Логика', value: 'match_type', sortable: false },
        {
          text: 'Условий', value: 'conditions', sortable: false, width: 100,
        },
        { text: 'Получатели', value: 'recipients', sortable: false },
        { text: 'Активность', value: 'is_active', width: 120 },
        { text: 'Обновлено', value: 'updated_at' },
        {
          text: '', value: 'actions', sortable: false, align: 'end', width: 140,
        },
      ],
      formDialog: false,
      form: null,
      formErrors: {},
      formError: '',
      saving: false,
      openingId: null,
      selectedRecipients: [],
      recipientEmail: '',
      resourceDialog: false,
      pendingResource: null,
      deleteDialog: false,
      deleteTarget: null,
      deleting: false,
      dryRunDialog: false,
      dryRunRuleId: null,
      dryRunDraft: null,
      dryRunJson: '{\n  \n}',
      dryRunJsonError: '',
      dryRunError: '',
      dryRunLoading: false,
      dryRunResult: null,
      triggers: [],
      triggersLoading: false,
      triggerStatuses: TRIGGER_STATUSES,
      triggerFilters: {
        rule_id: '',
        resource: '',
        status: '',
        date_from: '',
        date_to: '',
      },
      triggerHeaders: [
        { text: 'Дата', value: 'created_at', width: 160 },
        { text: 'Дата и время события', value: 'event_datetime', width: 190 },
        { text: 'Правило', value: 'rule_name' },
        { text: 'Тип события', value: 'resource' },
        { text: 'Филиал', value: 'company_id' },
        { text: 'resource_id', value: 'resource_id' },
        { text: 'Статус webhook', value: 'webhook_status' },
        { text: 'Доставка', value: 'status' },
        { text: 'Значения условий', value: 'matched_values', sortable: false },
        { text: 'Notification IDs', value: 'notification_ids', sortable: false },
        { text: 'Ошибка', value: 'error', sortable: false },
      ],
      pageError: '',
      snackbar: false,
      snackbarText: '',
      snackbarColor: 'primary',
    }
  },
  computed: {
    currentFields() {
      if (!this.form) return []
      const group = this.metadata.fields.find(item => item.resource === this.form.resource)
      return group ? group.fields : []
    },
    recipientEmailError() {
      if (!this.recipientEmail || this.isValidEmail(this.recipientEmail)) return ''
      return 'Введите корректный email'
    },
  },
  watch: {
    tab(value) {
      this.pageError = ''
      if (value === 1) this.loadTriggers()
    },
  },
  async created() {
    await this.loadMetadata()
    await this.loadRules()
  },
  methods: {
    toast(message, color = 'primary') {
      this.snackbarText = message
      this.snackbarColor = color
      this.snackbar = true
    },
    errorMessage(error, fallback) {
      if (error && error.response && error.response.status === 403) {
        return 'Недостаточно административных прав для выполнения операции'
      }
      const data = error && error.response && error.response.data
      if (typeof data === 'string') return data
      if (data && typeof data.detail === 'string') return data.detail
      if (data && typeof data.error === 'string') return data.error
      return fallback
    },
    assignFormErrors(error) {
      const data = error && error.response && error.response.data
      this.formErrors = {}
      this.formError = ''
      if (!data || typeof data !== 'object' || Array.isArray(data)) {
        this.formError = this.errorMessage(error, 'Не удалось сохранить правило')
        return
      }
      Object.entries(data).forEach(([key, value]) => {
        const message = Array.isArray(value) ? value.join(', ') : String(value)
        if (['name', 'resource', 'match_type', 'message_template', 'conditions', 'recipients'].includes(key)) {
          this.$set(this.formErrors, key, message)
        } else {
          this.formError = this.formError ? `${this.formError}; ${message}` : message
        }
      })
      if (!Object.keys(this.formErrors).length && !this.formError) {
        this.formError = this.errorMessage(error, 'Не удалось сохранить правило')
      }
    },
    fieldError(field) {
      return this.formErrors[field] ? [this.formErrors[field]] : []
    },
    clearFieldError(field) {
      if (!this.formErrors[field]) return
      this.$delete(this.formErrors, field)
    },
    async loadMetadata() {
      this.metadataLoading = true
      try {
        this.metadata = { ...emptyMetadata(), ...await Api.getWebhookRuleMetadata() }
      } catch (error) {
        this.pageError = this.errorMessage(error, 'Не удалось загрузить метаданные формы')
      } finally {
        this.metadataLoading = false
      }
    },
    async loadRules() {
      this.rulesLoading = true
      this.pageError = ''
      try {
        const data = await Api.getWebhookRules(cleanParams(this.ruleFilters))
        this.rules = (data && data.results) || []
      } catch (error) {
        this.rules = []
        this.pageError = this.errorMessage(error, 'Не удалось загрузить правила')
      } finally {
        this.rulesLoading = false
      }
    },
    resetRuleFilters() {
      this.ruleFilters = { resource: '', is_active: null }
      this.loadRules()
    },
    resourceTitle(value) {
      const item = this.metadata.resources.find(resource => resource.value === value)
      return item ? item.label : (value || '—')
    },
    matchTypeTitle(value) {
      const item = this.metadata.match_types.find(type => type.value === value)
      return item ? item.label : value
    },
    companyTitle(value) {
      if (value === null || value === undefined || value === '') return '—'
      const item = this.metadata.companies.find(company => String(company.value) === String(value))
      return item ? item.label : value
    },
    formatDateTime(value) {
      if (!value) return '—'
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return value
      return new Intl.DateTimeFormat('ru-RU', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      }).format(date)
    },
    emptyForm() {
      return {
        id: null,
        name: '',
        resource: this.metadata.resources.length ? this.metadata.resources[0].value : '',
        match_type: this.metadata.match_types.length ? this.metadata.match_types[0].value : 'ALL',
        message_template: '',
        is_active: true,
        conditions: [],
      }
    },
    openCreate() {
      this.form = this.emptyForm()
      this.formErrors = {}
      this.formError = ''
      this.selectedRecipients = []
      this.recipientEmail = ''
      this.formDialog = true
    },
    async openEdit(item) {
      this.openingId = item.id
      this.pageError = ''
      try {
        const rule = await Api.getWebhookRule(item.id)
        this.form = {
          id: rule.id,
          name: rule.name || '',
          resource: rule.resource || '',
          match_type: rule.match_type || 'ALL',
          message_template: rule.message_template || '',
          is_active: Boolean(rule.is_active),
          conditions: (rule.conditions || []).map(condition => newCondition(condition)),
        }
        this.selectedRecipients = (rule.recipients || []).map(recipient => ({
          email: String(recipient.email || '').trim().toLowerCase(),
        }))
        this.recipientEmail = ''
        this.formErrors = {}
        this.formError = ''
        this.formDialog = true
      } catch (error) {
        this.pageError = this.errorMessage(error, 'Не удалось открыть правило')
      } finally {
        this.openingId = null
      }
    },
    addCondition() {
      const field = this.currentFields[0]
      const condition = newCondition({
        field: field ? field.key : '',
        operator: field && field.operators.length ? field.operators[0] : '',
        value: null,
      })
      if (EMPTY_OPERATORS.includes(condition.operator)) condition.value = null
      else if (this.isMultiValue(condition)) condition.value = []
      this.form.conditions.push(condition)
      this.clearFieldError('conditions')
    },
    removeCondition(index) {
      this.form.conditions.splice(index, 1)
    },
    moveCondition(index, direction) {
      const next = index + direction
      if (next < 0 || next >= this.form.conditions.length) return
      const conditions = [...this.form.conditions]
      const item = conditions.splice(index, 1)[0]
      conditions.splice(next, 0, item)
      this.form.conditions = conditions
    },
    conditionField(condition) {
      return this.currentFields.find(field => field.key === condition.field)
    },
    operatorsFor(condition) {
      const field = this.conditionField(condition)
      if (!field) return []
      return field.operators.map(value => {
        const operator = this.metadata.operators.find(item => item.value === value)
        return operator || { value, label: value }
      })
    },
    onConditionFieldChange(condition) {
      const operators = this.operatorsFor(condition)
      const updated = {
        ...condition,
        operator: operators.length ? operators[0].value : '',
      }
      updated.value = this.isMultiValue(updated) ? [] : null
      const index = this.form.conditions.findIndex(item => item.localKey === condition.localKey)
      if (index !== -1) this.$set(this.form.conditions, index, updated)
      this.clearFieldError('conditions')
    },
    onConditionOperatorChange(condition) {
      const index = this.form.conditions.findIndex(item => item.localKey === condition.localKey)
      if (index !== -1) {
        this.$set(this.form.conditions, index, {
          ...condition,
          value: this.isMultiValue(condition) ? [] : null,
        })
      }
      this.clearFieldError('conditions')
    },
    showsConditionValue(condition) {
      return condition.operator && !EMPTY_OPERATORS.includes(condition.operator)
    },
    isMultiValue(condition) {
      const field = this.conditionField(condition)
      if (!field) return false
      return LIST_OPERATORS.includes(condition.operator)
        || (String(field.type).startsWith('list_') && ['eq', 'neq', 'gt', 'lt', 'gte', 'lte'].includes(condition.operator))
    },
    isNumericCondition(condition) {
      const field = this.conditionField(condition)
      if (!field) return false
      return ['integer', 'number', 'list_integer', 'list_number'].includes(field.type)
    },
    requestResourceChange(resource) {
      if (!this.form || resource === this.form.resource) return
      this.clearFieldError('resource')
      if (!this.form.conditions.length) {
        this.form.resource = resource
        return
      }
      this.pendingResource = resource
      this.resourceDialog = true
    },
    confirmResourceChange() {
      this.form.resource = this.pendingResource
      this.form.conditions = []
      this.pendingResource = null
      this.resourceDialog = false
      this.clearFieldError('conditions')
    },
    isValidEmail(value) {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || '').trim())
    },
    addRecipientEmail() {
      const email = String(this.recipientEmail || '').trim().toLowerCase()
      if (!this.isValidEmail(email)) return
      if (!this.selectedRecipients.some(recipient => recipient.email === email)) {
        this.selectedRecipients.push({ email })
      }
      this.recipientEmail = ''
      this.clearFieldError('recipients')
    },
    removeRecipient(recipient) {
      this.selectedRecipients = this.selectedRecipients.filter(item => item.email !== recipient.email)
    },
    insertTemplateField(field) {
      if (!this.form) return
      const token = `{${field}}`
      const component = this.$refs.messageTemplate
      const input = component && component.$refs && component.$refs.input
      if (!input || input.selectionStart === undefined) {
        this.form.message_template += token
        return
      }
      const start = input.selectionStart
      const end = input.selectionEnd
      const value = this.form.message_template || ''
      this.form.message_template = `${value.slice(0, start)}${token}${value.slice(end)}`
      this.$nextTick(() => {
        input.focus()
        input.setSelectionRange(start + token.length, start + token.length)
      })
    },
    templateFieldTitle(field) {
      if (field === 'event_datetime') return 'Дата и время события'
      return `{${field}}`
    },
    normalizedConditionValue(condition) {
      if (!this.showsConditionValue(condition)) return null
      const field = this.conditionField(condition)
      const type = field ? field.type : ''
      if (this.isMultiValue(condition)) {
        const values = Array.isArray(condition.value) ? condition.value : []
        if (type === 'list_integer' || type === 'integer') return values.map(value => Number(value))
        if (type === 'list_number' || type === 'number') return values.map(value => Number(value))
        return values
      }
      if (type === 'integer') return Number(condition.value)
      if (type === 'number') return Number(condition.value)
      return condition.value
    },
    rulePayload() {
      return {
        name: this.form.name,
        resource: this.form.resource,
        match_type: this.form.match_type,
        message_template: this.form.message_template,
        is_active: this.form.is_active,
        conditions: this.form.conditions.map(condition => ({
          field: condition.field,
          operator: condition.operator,
          value: this.normalizedConditionValue(condition),
        })),
        recipients: this.selectedRecipients.map(recipient => ({
          email: String(recipient.email || '').trim().toLowerCase(),
        })),
      }
    },
    validateForm() {
      const errors = {}
      if (!String(this.form.name || '').trim()) errors.name = 'Обязательное поле'
      if (!this.form.resource) errors.resource = 'Выберите тип события'
      if (!this.form.match_type) errors.match_type = 'Выберите логику условий'
      if (!this.form.conditions.length) errors.conditions = 'Добавьте хотя бы одно условие'
      const invalidCondition = this.form.conditions.find(condition => {
        if (!condition.field || !condition.operator) return true
        if (!this.showsConditionValue(condition)) return false
        if (this.isMultiValue(condition)) return !Array.isArray(condition.value) || !condition.value.length
        return condition.value === null || condition.value === undefined || condition.value === ''
      })
      if (invalidCondition) errors.conditions = 'Заполните поле, оператор и значение каждого условия'
      if (!this.selectedRecipients.length) {
        errors.recipients = 'Добавьте хотя бы один email'
      } else {
        const emails = this.selectedRecipients.map(recipient => String(recipient.email || '').trim().toLowerCase())
        if (emails.some(email => !this.isValidEmail(email))) {
          errors.recipients = 'Укажите корректный email для каждого получателя'
        } else if (new Set(emails).size !== emails.length) {
          errors.recipients = 'Email получателей не должны повторяться'
        }
      }
      this.formErrors = errors
      return !Object.keys(errors).length
    },
    async saveRule() {
      if (this.saving || !this.form || !this.validateForm()) return
      this.saving = true
      this.formError = ''
      try {
        const payload = this.rulePayload()
        if (this.form.id) await Api.updateWebhookRule(this.form.id, payload)
        else await Api.createWebhookRule(payload)
        this.formDialog = false
        this.toast(this.form.id ? 'Правило обновлено' : 'Правило создано', 'success')
        await this.loadRules()
      } catch (error) {
        this.assignFormErrors(error)
      } finally {
        this.saving = false
      }
    },
    requestDelete(rule) {
      this.deleteTarget = rule
      this.deleteDialog = true
    },
    async confirmDelete() {
      if (!this.deleteTarget || this.deleting) return
      this.deleting = true
      try {
        await Api.deleteWebhookRule(this.deleteTarget.id)
        this.deleteDialog = false
        this.deleteTarget = null
        this.toast('Правило удалено', 'success')
        await this.loadRules()
      } catch (error) {
        this.deleteDialog = false
        this.pageError = this.errorMessage(error, 'Не удалось удалить правило')
      } finally {
        this.deleting = false
      }
    },
    openDryRun(rule) {
      if (!rule && this.form && !this.validateForm()) return
      this.dryRunRuleId = rule ? rule.id : null
      this.dryRunDraft = rule ? null : this.rulePayload()
      this.dryRunJson = '{\n  \n}'
      this.dryRunJsonError = ''
      this.dryRunError = ''
      this.dryRunResult = null
      this.dryRunDialog = true
    },
    async runDryRun() {
      if (this.dryRunLoading) return
      let webhookBody
      try {
        webhookBody = JSON.parse(this.dryRunJson)
        if (!webhookBody || Array.isArray(webhookBody) || typeof webhookBody !== 'object') {
          throw new Error('not-object')
        }
      } catch (error) {
        this.dryRunJsonError = 'Введите корректный JSON-объект'
        return
      }
      this.dryRunLoading = true
      this.dryRunError = ''
      this.dryRunResult = null
      try {
        const payload = this.dryRunRuleId
          ? { rule_id: this.dryRunRuleId, webhook_body: webhookBody }
          : { rule: this.dryRunDraft, webhook_body: webhookBody }
        this.dryRunResult = await Api.testWebhookRule(payload)
      } catch (error) {
        this.dryRunError = this.errorMessage(error, 'Не удалось выполнить dry-run')
      } finally {
        this.dryRunLoading = false
      }
    },
    async loadTriggers() {
      this.triggersLoading = true
      this.pageError = ''
      try {
        const params = cleanParams({
          ...this.triggerFilters,
          date_from: this.toIsoDate(this.triggerFilters.date_from),
          date_to: this.toIsoDate(this.triggerFilters.date_to),
        })
        const data = await Api.getWebhookRuleTriggers(params)
        this.triggers = (data && data.results) || []
      } catch (error) {
        this.triggers = []
        this.pageError = this.errorMessage(error, 'Не удалось загрузить журнал')
      } finally {
        this.triggersLoading = false
      }
    },
    resetTriggerFilters() {
      this.triggerFilters = {
        rule_id: '',
        resource: '',
        status: '',
        date_from: '',
        date_to: '',
      }
      this.loadTriggers()
    },
    toIsoDate(value) {
      if (!value) return ''
      const date = new Date(value)
      return Number.isNaN(date.getTime()) ? value : date.toISOString()
    },
    statusColor(status) {
      return {
        PROCESSING: 'info',
        SENT: 'success',
        PARTIAL: 'warning',
        SKIPPED: 'grey',
        FAILED: 'error',
      }[status] || 'grey'
    },
    formatJson(value) {
      if (value === null || value === undefined) return '—'
      try {
        return JSON.stringify(value, null, 2)
      } catch (error) {
        return String(value)
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.webhook-page-header,
.webhook-filter-actions,
.template-fields {
  gap: 8px;
}

.webhook-filter-actions {
  align-items: center;
}

.condition-row {
  min-height: 48px;

  > [class*='col-'] {
    min-width: 0;
  }
}

.condition-actions {
  gap: 2px;
}

.status-help-icon {
  cursor: help;
}

.recipient-email-control {
  max-width: 560px;
  gap: 12px;
}

.recipients-card {
  max-width: 560px;
}

.selected-recipient {
  min-height: 46px !important;
  border-bottom: 1px solid rgba(127, 127, 127, 0.2);

  &:last-child {
    border-bottom: 0;
  }
}

.selected-recipient ::v-deep {
  .v-list-item__content,
  .v-list-item__action {
    margin: 6px 0;
    padding: 0;
  }

  .v-list-item__title {
    font-size: 14px;
  }
}

.webhook-json-cell {
  max-width: 340px;
  max-height: 140px;
  overflow: auto;
  margin: 8px 0;
  white-space: pre-wrap;
  font-size: 12px;
}

.webhook-result {
  max-height: 260px;
  overflow: auto;
  padding: 12px;
  margin: 4px 0 0;
  border-radius: 4px;
  background: rgba(127, 127, 127, 0.08);
  white-space: pre-wrap;
}

.trigger-table ::v-deep .v-data-table__wrapper {
  overflow-x: auto;
}

.webhook-rules-table ::v-deep {
  thead tr {
    height: 52px;
  }

  thead tr th {
    height: 52px !important;
    padding-top: 0 !important;
    padding-bottom: 0 !important;
    vertical-align: middle !important;
    white-space: nowrap;
  }

  thead tr th > span {
    display: inline-flex;
    min-height: 52px;
    align-items: center;
  }
}

@media (max-width: 600px) {
  .webhook-page-header {
    flex-direction: column;
  }

  .webhook-filter-actions {
    justify-content: flex-end;
  }

  .recipient-email-control {
    flex-direction: column;
    gap: 0;
  }

  .recipients-card,
  .recipient-email-control {
    max-width: none;
  }
}
</style>
